import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@ncoder/ui';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmModalConfig {
  title?: string;
  content: React.ReactNode;
  okText?: string;
  cancelText?: string;
  okButtonProps?: React.ComponentProps<typeof Button>;
  cancelButtonProps?: React.ComponentProps<typeof Button>;
}

export interface ConfirmModalInstance {
  confirm: (config: ConfirmModalConfig) => Promise<boolean>;
  warning: (config: ConfirmModalConfig) => Promise<boolean>;
  info: (config: ConfirmModalConfig) => Promise<boolean>;
  error: (config: ConfirmModalConfig) => Promise<boolean>;
}

export function useConfirmModal(): [ConfirmModalInstance, React.ReactNode] {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<ConfirmModalConfig | null>(null);
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);
  const [type, setType] = useState<'confirm' | 'warning' | 'info' | 'error'>('confirm');

  const createModal = useCallback(
    (modalType: 'confirm' | 'warning' | 'info' | 'error') =>
      (modalConfig: ConfirmModalConfig): Promise<boolean> => {
        return new Promise((resolve) => {
          setConfig(modalConfig);
          setType(modalType);
          setOpen(true);
          setResolveRef(() => resolve);
        });
      },
    []
  );

  const handleOk = useCallback(() => {
    setOpen(false);
    if (resolveRef) {
      resolveRef(true);
      setResolveRef(null);
    }
  }, [resolveRef]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    if (resolveRef) {
      resolveRef(false);
      setResolveRef(null);
    }
  }, [resolveRef]);

  const modal: ConfirmModalInstance = {
    confirm: createModal('confirm'),
    warning: createModal('warning'),
    info: createModal('info'),
    error: createModal('error'),
  };

  const getTitle = () => {
    if (config?.title) return config.title;
    switch (type) {
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Info';
      case 'error':
        return 'Error';
      default:
        return 'Confirm';
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'warning':
        return 'text-yellow-600';
      case 'info':
        return 'text-blue-600';
      case 'error':
        return 'text-destructive';
      default:
        return 'text-primary';
    }
  };

  const contextHolder = (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className={`h-5 w-5 ${getIconColor()}`} />
            {getTitle()}
          </DialogTitle>
          <DialogDescription>{config?.content}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            {...(config?.cancelButtonProps || {})}
          >
            {config?.cancelText || 'Cancel'}
          </Button>
          <Button
            variant={type === 'error' ? 'destructive' : 'default'}
            size="sm"
            onClick={handleOk}
            {...(config?.okButtonProps || {})}
          >
            {config?.okText || 'OK'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return [modal, contextHolder];
}

